import { t } from "@lingui/macro";
import { useCallback, useState } from "react";

import { ScreenFooter } from "@whoosh/design-system/src/apps/PatronApp/layout/ScreenFooter";
import { Button } from "@whoosh/design-system/src/components/controls";
import { TextArea } from "@whoosh/design-system/src/components/controls/TextArea";
import { Loader } from "@whoosh/design-system/src/components/display";
import { AddNoteIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import { StandardModal } from "@whoosh/design-system/src/components/overlay/StandardModal";
import { Text, VStack } from "@whoosh/design-system/src/components/primitive";

import { MemberNoteToTheStaffInfo } from "./MemberNoteToTheStaffInfo";

type Props = {
  buttonText: string;
  placeHolderText: string;
  notes: string;
  onSave: (notes: string) => void;
  disabled?: boolean;
  isLoading?: boolean;
  canUpdate?: boolean | null;
};

export const MemberNoteToTheStaff = ({
  buttonText,
  placeHolderText,
  notes,
  onSave,
  disabled = false,
  isLoading = false,
  canUpdate,
}: Props) => {
  const [newNotes, setNewNotes] = useState(notes);

  const [notesOpen, setNotesOpen] = useState(false);

  const onClear = useCallback(() => {
    setNewNotes("");
    onSave("");
  }, [onSave]);

  return canUpdate ? (
    <>
      {isLoading ? (
        <VStack height={180}>
          <Loader />
        </VStack>
      ) : (
        <VStack gap={4}>
          {notes ? (
            <MemberNoteToTheStaffInfo {...{ setNotesOpen, onClear, notes }} />
          ) : null}

          {!notes && !disabled ? (
            <Button
              size="small"
              width="100%"
              onPress={() => setNotesOpen(true)}
              variant="secondary"
              testID={"booking-detail-add-note-btn"}
              leftIcon={<AddNoteIcon />}
              accessibilityLabel={t`Add Note button`}
            >
              {buttonText}
            </Button>
          ) : null}
        </VStack>
      )}
      <StandardModal
        hideFooterPadding
        upperTitle={t`Note for Staff`}
        isOpen={notesOpen}
        onClose={() => setNotesOpen(false)}
        content={
          <VStack gap={2} px={6}>
            <Text variant="mainTiny" color="text-secondary">
              {t`Note for Staff`}
            </Text>

            <TextArea
              name="note"
              value={newNotes}
              placeholder={placeHolderText}
              onChange={setNewNotes}
              testID="member-note-to-staff-input"
            />
          </VStack>
        }
        footerContent={
          <ScreenFooter>
            <Button
              testID="booking-detail-update-note"
              width="100%"
              onPress={() => {
                onSave(newNotes);
                setNotesOpen(false);
              }}
              accessibilityLabel={t`Continue button`}
            >{t`Continue`}</Button>
          </ScreenFooter>
        }
      />
    </>
  ) : null;
};
